/**
 * The Weekly review page's types and pure helpers. No imports and only erasable
 * types, so node --test loads this file directly (test/fulfillment-ui.test.js).
 */
export type Finding = {
  id: number; customer_id: string; kind: string; rule: string; severity: "high" | "medium" | "low";
  title: string; detail: string | null; evidence: Record<string, any>; proposal: { action_type: string } | null;
  status: "open" | "queued" | "dismissed" | "tasked"; approval_id: number | null; task_id: number | null;
  decided_at: string | null; decided_by_name: string | null; dismiss_reason?: string | null;
  /** The account's name as the run that raised it knew it (its label, or its ID). */
  account_name?: string | null;
};
export type Run = {
  id: number; status: "running" | "ok" | "partial" | "failed"; started_at: string; finished_at: string | null;
  period_from: string; period_to: string; triggered_by: string;
  accounts: Array<{ customer_id: string; name?: string; status: string; error?: string; failed_checks?: Record<string, string> }>;
};
/** One step of the audit SOP checklist (utils/adsSop.js AUDIT_STEPS) as the latest review left it. */
export type SopStep = {
  step: number; title: string; kb: string; status: "issues" | "clear" | "unread" | "person" | "later";
  open: number; person: string | null; failed: string[];
};
export type Week = { run: Run | null; findings: Finding[]; summary: { open: number; wasted_spend: number }; linked?: boolean; sop?: SopStep[] };

const AGENCY_TZ = "America/New_York"; // api.ts AGENCY_TZ; this file has no imports
const CHECK_LABEL: Record<string, string> = {
  searchTerms: "Search terms", converters: "Converting searches", placements: "Placements", keywords: "Quality Score",
  campaigns: "Impression share", ads: "Ads", changes: "Change history", settings: "Campaign settings", aiMax: "AI Max",
  keywordsAll: "Keywords", conversionActions: "Conversion actions", days: "Day of week", hours: "Hour of day", devices: "Devices",
  callAssets: "Call assets", pmax: "Performance Max", assetLabels: "Ad assets", userLists: "Customer Match", customer: "Account settings",
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SEVERITY_RANK = { high: 0, medium: 1, low: 2 } as const;
/** Items a grouped finding names in its line; the rest open under "Show N more". */
const IN_LINE = 3;
export const UNDO_MINUTES = 60; // utils/fulfillment.js: a dismissal can be taken back for an hour

const n = (v: unknown) => Number(v || 0);
const usd = (v: unknown) => `$${n(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortDay = (s: unknown) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || "")); return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : ""; };
const words = (s: unknown) => String(s || "").toLowerCase().replace(/_/g, " ");
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/** "1 click", "2 clicks", "1,234 impressions". */
export function count(v: unknown, one: string, many = `${one}s`) {
  const x = n(v);
  return `${x.toLocaleString("en-US")} ${x === 1 ? one : many}`;
}
/** Google counts fractions of a conversion: 3.99 reads "4 conversions", 24.14 "24.1 conversions", 0 "no conversions". */
export function conversions(v: unknown) {
  const x = Math.round(n(v) * 10) / 10;
  return x === 0 ? "no conversions" : `${x.toLocaleString("en-US")} ${x === 1 ? "conversion" : "conversions"}`;
}

export function formatCustomerId(id: string) {
  return String(id || "").replace(/^(\d{3})(\d{3})(\d{4})$/, "$1-$2-$3");
}

/** An account as people know it: its label, or its ID written 939-508-1472 when it has none. */
export function accountLabel(customerId: string, name?: string | null) {
  return name && name !== customerId && !/^\d+$/.test(name) ? name : formatCustomerId(customerId);
}

/** The finding's account: its own run's name for it, else the latest run's, else the ID. */
export function findingAccount(f: Finding, run: Pick<Run, "accounts"> | null) {
  return accountLabel(f.customer_id, f.account_name || run?.accounts.find((a) => a.customer_id === f.customer_id)?.name);
}

/** A client with one Google Ads account doesn't need the account named on every finding. */
export function hasSeveralAccounts(run: Pick<Run, "accounts"> | null, findings: Finding[]) {
  return new Set([...(run?.accounts || []).map((a) => a.customer_id), ...findings.map((f) => f.customer_id)]).size > 1;
}

/** "Oct 2", or "Oct 2, 2025" outside this year. A calendar day stays itself; a timestamp is read on the agency's clock. */
export function shortDate(value: string | null | undefined, now = new Date()) {
  if (!value) return "";
  const yearNow = Number(new Intl.DateTimeFormat("en-US", { timeZone: AGENCY_TZ, year: "numeric" }).format(now));
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  let y: number, m: number, d: number;
  if (day) [y, m, d] = [Number(day[1]), Number(day[2]), Number(day[3])];
  else {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: AGENCY_TZ, year: "numeric", month: "numeric", day: "numeric" })
      .formatToParts(date).map((p) => [p.type, p.value]));
    [y, m, d] = [Number(parts.year), Number(parts.month), Number(parts.day)];
  }
  return `${MONTHS[m - 1]} ${d}${y === yearNow ? "" : `, ${y}`}`;
}

/** "Last reviewed Oct 2 for Sep 2 to Oct 1", or when a review is under way, "Review running since 3:15 PM ET". */
export function reviewStatus(run: Run | null, now = new Date()) {
  if (!run) return "";
  if (run.status === "running") {
    const time = new Intl.DateTimeFormat("en-US", { timeZone: AGENCY_TZ, hour: "numeric", minute: "2-digit" }).format(new Date(run.started_at));
    return `Review running since ${time} ET`;
  }
  return `Last reviewed ${shortDate(run.started_at, now)} for ${shortDate(run.period_from, now)} to ${shortDate(run.period_to, now)}`;
}

/** Open findings with a change ready for Approvals, those a person must decide, then what was handled. */
export function groupFindings(findings: Finding[]) {
  return {
    ready: findings.filter((f) => f.status === "open" && f.proposal),
    person: findings.filter((f) => f.status === "open" && !f.proposal),
    handled: findings.filter((f) => f.status !== "open"),
  };
}

/**
 * The open groups, the one holding the most severe finding first (on a tie, the
 * changes ready to send), then what was handled. Empty groups are left out.
 */
export function orderedGroups(findings: Finding[]) {
  const g = groupFindings(findings);
  const worst = (items: Finding[]) => Math.min(3, ...items.map((f) => SEVERITY_RANK[f.severity] ?? 2));
  const open = [{ key: "ready" as const, items: g.ready }, { key: "person" as const, items: g.person }]
    .filter((x) => x.items.length)
    .sort((a, b) => worst(a.items) - worst(b.items));
  return [...open, ...(g.handled.length ? [{ key: "handled" as const, items: g.handled }] : [])];
}

/** A dismissal made in the last hour can be undone. */
export function canUndo(f: Finding, now = Date.now()) {
  return f.status === "dismissed" && !!f.decided_at && now - Date.parse(f.decided_at) < UNDO_MINUTES * 60 * 1000;
}

/** The rule line split around its playbook codes, which the page links: ["", "KB-029", " §2: ..."]. "KB-029U" is the GML 2026 update, its own page. */
export function ruleParts(rule: string) {
  return String(rule || "").split(/(KB-\d{3}U?)/);
}

/** A checklist step's status in words: "2 to fix", "Nothing to fix", "Couldn't read", or what to check by hand. */
export function sopStatusText(s: SopStep) {
  if (s.status === "issues") return `${s.open.toLocaleString("en-US")} to fix`;
  if (s.status === "unread") return "Couldn't read";
  if (s.status === "later") return "Next review"; // the last review ran before this check existed
  if (s.status === "person") return "Check by hand";
  return "Nothing to fix";
}

/** The reads a checklist step missed, in words: "Call assets, Hour of day". */
export function sopFailedText(s: SopStep) {
  return s.failed.map((k) => CHECK_LABEL[k] || k).join(", ");
}

/**
 * The budget route refuses a superadmin's instant raise on a campaign with a bid target
 * until someone confirms the target still fits (KB-019U §9): 409 with needs_target_review.
 * api() keeps only the message and status, so the KB-019U §9 citation in it marks it too.
 */
export function needsTargetReview(e: unknown) {
  const x = e as { status?: number; message?: string; data?: { needs_target_review?: boolean }; needs_target_review?: boolean } | null;
  if (!x || x.status !== 409) return false;
  return x.data?.needs_target_review === true || x.needs_target_review === true || /KB-019U §9/.test(String(x.message || ""));
}

// ── Automatic changes, in words ─────────────────────────────────────────────
type ChangeWords = { key: string; one: string; many: (count: number) => string };
const VERB: Record<string, string> = { CREATE: "Added", REMOVE: "Removed", UPDATE: "Changed" };
const article = (noun: string) => (/^[aeiou]/i.test(noun) ? "an" : "a");
const countable = (op: string, one: string, many: string): ChangeWords => {
  const verb = VERB[op] || "Changed";
  return { key: `${verb}|${one}`, one: `${verb} ${article(one)} ${one}`, many: (c) => `${verb} ${c} ${many}` };
};
const setting = (phrase: string): ChangeWords =>
  ({ key: phrase, one: phrase, many: (c) => `${phrase} ${c === 2 ? "twice" : `${c} times`}` });

/** One change event (change_event in Google Ads) as a person would say it. */
function changeWords(c: Record<string, any>): ChangeWords {
  const resource = String(c.resource || "").toUpperCase();
  const op = String(c.operation || "").toUpperCase();
  const fields = String(c.fields || "");
  const keyword = /keyword/i.test(fields);
  switch (resource) {
    case "AD_GROUP_CRITERION":
      return keyword ? countable(op, "keyword", "keywords") : setting("Changed ad group targeting");
    case "CAMPAIGN_CRITERION":
      // A keyword set on a campaign is a negative keyword.
      return keyword ? countable(op, "negative keyword", "negative keywords") : setting("Changed campaign targeting");
    case "AD":
    case "AD_GROUP_AD":
      return countable(op, "ad", "ads");
    case "AD_GROUP":
      return op === "UPDATE" ? setting("Changed ad group settings") : countable(op, "ad group", "ad groups");
    case "AD_GROUP_BID_MODIFIER":
      return countable(op, "bid adjustment", "bid adjustments");
    case "CAMPAIGN_BUDGET":
      return setting("Changed the budget");
    case "CAMPAIGN":
      if (op !== "UPDATE") return countable(op, "campaign", "campaigns");
      if (/targetCpa/i.test(fields)) return setting("Changed the target CPA");
      if (/targetRoas/i.test(fields)) return setting("Changed the target ROAS");
      if (/bidding|maximize|manualCpc|targetSpend/i.test(fields)) return setting("Changed the bid strategy");
      if (/budget/i.test(fields)) return setting("Changed the budget");
      return setting("Changed campaign settings");
    default:
      if (/ASSET/.test(resource)) return countable(op, "asset", "assets");
      if (/FEED/.test(resource)) return countable(op, "extension", "extensions");
      return countable(op, words(resource) || "setting", `${words(resource) || "setting"}s`);
  }
}

type Item = { text: string; count: number };

/** Automatic changes merged: the same change on the same day (and campaign) is one item with its count. */
function changeGroups(changes: unknown): Item[] {
  const groups = new Map<string, { words: ChangeWords; count: number; campaign: string; day: string }>();
  for (const c of Array.isArray(changes) ? changes : []) {
    const w = changeWords(c || {});
    const campaign = String(c?.campaign_name || "");
    const day = shortDay(c?.at);
    const key = `${w.key}|${campaign}|${day}`;
    const cur = groups.get(key) || { words: w, count: 0, campaign, day };
    cur.count += 1;
    groups.set(key, cur);
  }
  return [...groups.values()].map((g) => ({
    text: `${g.count === 1 ? g.words.one : g.words.many(g.count)}${g.campaign ? ` in ${g.campaign}` : ""}${g.day ? ` on ${g.day}` : ""}`,
    count: g.count,
  }));
}

/**
 * Google's automatic changes in words, repeats merged: "Removed 2 keywords on Sep 23",
 * "Changed the target CPA in Search Junk on Sep 5". Changes keep their order (newest first).
 */
export function changeItems(changes: unknown): string[] {
  return changeGroups(changes).map((g) => g.text);
}

/**
 * The numbers behind a finding as one short line, and for a grouped finding the
 * items that don't fit in it (opened with `moreLabel`).
 */
export function evidence(f: Finding): { line: string; more: string[]; moreLabel: string } {
  const e = f.evidence || {};
  const none = { more: [] as string[], moreLabel: "" };
  // A list: its first items in the line; the rest, and any the review didn't keep, under "Show N more".
  const list = (items: Item[], total: unknown, lower = false) => {
    const kept = items.reduce((s, x) => s + x.count, 0);
    const unlisted = Math.max(0, n(total) - kept);
    const rest = items.slice(IN_LINE);
    const more = [...rest.map((x) => x.text), ...(unlisted > 0 ? [`${unlisted.toLocaleString("en-US")} more in Google Ads`] : [])];
    const hidden = rest.reduce((s, x) => s + x.count, 0) + unlisted;
    return {
      line: items.slice(0, IN_LINE).map((x, i) => (lower && i > 0 ? lowerFirst(x.text) : x.text)).join("; "),
      more,
      moreLabel: more.length ? `Show ${hidden.toLocaleString("en-US")} more` : "",
    };
  };
  const each = (texts: string[]): Item[] => texts.map((text) => ({ text, count: 1 }));
  const arr = (v: unknown): any[] => (Array.isArray(v) ? v : []);
  const days = n(e.days) || 30; // search terms and day, hour and device segments cover 90 days
  const perConversion = (cpa: unknown) => (cpa === null || cpa === undefined ? "no conversions" : `${usd(cpa)} a conversion`);
  const campaignItems = (budget = false) => list(each(arr(e.campaigns).map((c: any) => (budget && c.daily_budget ? `${c.campaign}: ${usd(c.daily_budget)} a day` : String(c.campaign)))), e.total);
  switch (f.kind) {
    case "wasted_search_term":
    case "intent_search_term":
      return { line: `${usd(e.cost)} spent, ${count(e.clicks, "click")}, no conversions in ${days} days`, ...none };
    case "promote_search_term":
      return { line: `${conversions(e.conversions)} for ${usd(e.cost)} in ${days} days`, ...none };
    case "placement":
      return { line: `${usd(e.cost)} spent, ${count(e.impressions, "impression")}, ${conversions(e.conversions)} in ${days} days`, ...none };
    case "placement_group": {
      // Google names apps "Mobile App: Phone Call (Google Play), by ..."; under "Ads ran in 38 mobile apps" the prefix is noise.
      const items = arr(e.placements)
        .map((p: any) => `${String(p.placement_name || p.url || p.placement).replace(/^Mobile App:\s*/i, "")}: ${usd(p.cost)}`);
      return {
        line: `${usd(e.cost)} spent, ${count(e.impressions, "impression")}, ${conversions(e.conversions)} in ${days} days`,
        more: items, moreLabel: items.length ? `Show all ${items.length.toLocaleString("en-US")}` : "",
      };
    }
    case "budget_lost":
    case "rank_lost":
      return {
        line: `Impression share ${n(e.impression_share)}%, lost ${n(e.lost_to_budget)}% to budget and ${n(e.lost_to_rank)}% to rank. ${usd(e.cost)} spent, ${conversions(e.conversions)} in ${days} days.`,
        ...none,
      };
    case "low_quality_score":
      return list(each(arr(e.keywords).map((k: any) => `${k.keyword} (QS ${k.quality_score}, ${usd(k.cost)} spent${arr(k.below).length ? `; ${arr(k.below).join(", ")} below average` : ""})`)), e.total);
    case "poor_ad_strength":
      return list(each(arr(e.ads).map((a: any) => `${a.ad_group} in ${a.campaign}`)), e.total);
    case "auto_applied_changes":
      return list(changeGroups(e.changes), e.total, true);
    case "cannibal_terms":
      // Brand searches outside the brand campaign, or one search spending in several campaigns or ad groups.
      if (Array.isArray(e.terms)) return list(each(arr(e.terms).map((t: any) => `"${t.search_term}" in ${t.campaign}: ${usd(t.cost)}`)), e.total);
      return list(each(arr(e.overlaps).map((o: any) => `"${o.term}": ${arr(o.in).map((x: any) => `${x.ad_group ? `${x.ad_group} (${x.campaign})` : x.campaign} ${usd(x.cost)}`).join(", ")}`)), e.total);
    case "duplicate_keywords":
      return list(each(arr(e.keywords).map((k: any) => `${k.keyword} (${words(k.match_type)}): ${arr(k.in).map((x: any) => `${x.ad_group} in ${x.campaign}${x.quality_score ? `, QS ${x.quality_score}` : ""}`).join("; ")}`)), e.total);
    case "bidding_data":
      if (e.reason === "budget_limited") {
        const target = e.target_cpa ? `Target CPA ${usd(e.target_cpa)}, real ${usd(e.cpa)} a conversion` : `Target ROAS ${n(e.target_roas)}%, real ${n(e.roas)}%`;
        return { line: `${target} over ${days} days. Lost ${n(e.lost_to_budget)}% of impressions to budget.`, ...none };
      }
      return { line: `${e.bidding || "Bid target"}${e.target_cpa ? ` at ${usd(e.target_cpa)}` : e.target_roas ? ` at ${n(e.target_roas)}%` : ""}. ${conversions(e.conversions)} for ${usd(e.cost)} in ${days} days.`, ...none };
    case "broad_match":
      return {
        line: `Broad match took ${n(e.broad_share)}% of ${usd(e.cost)} spent; ${conversions(e.conversions)} in ${days} days${e.bidding ? `; bidding: ${e.bidding}` : ""}.`,
        more: arr(e.keywords).map((k: any) => `${k.keyword} (${k.ad_group}): ${usd(k.cost)}`),
        moreLabel: arr(e.keywords).length ? `Show ${count(arr(e.keywords).length, "keyword")}` : "",
      };
    case "low_budget":
      return campaignItems(true);
    case "ghost_campaign":
      return n(e.total) > 1 ? campaignItems() : { line: "", ...none };
    case "brand_campaign":
      return n(e.total) > 1 ? campaignItems(true) : { line: "", ...none };
    case "pmax":
      if (e.reason === "share")
        return { line: `Performance Max: ${n(e.share)}% of spend, ${perConversion(e.pmax_cpa)}. Search: ${perConversion(e.search_cpa)}. Last ${days} days.`, ...none };
      if (e.reason === "poor_assets") return list(each(arr(e.asset_groups).map((g: any) => `${g.asset_group} in ${g.campaign}`)), e.total);
      return n(e.total) > 1 ? campaignItems() : { line: "", ...none };
    case "conversion_tracking":
      if (e.reason === "double") return list(each(arr(e.pairs).map((p: any) => `${p.ga4} and ${p.tag} (${words(p.category)})`)), e.total);
      return list(each(arr(e.actions).map((a: any) => `${a.name} (${words(a.category) || words(a.type)})`)), e.total);
    case "day_of_week":
      if (e.reason === "no_weekend") return list(each(arr(e.campaigns).map((c: any) => `${c.campaign}: ${usd(c.cost)} on weekdays, none on weekends`)), e.total);
      if (e.reason === "concentration") return { line: `${e.day} brought ${n(e.share)}% of ${conversions(e.total_conversions)} in ${days} days.`, ...none };
      return { line: `Weekdays ${usd(e.weekday_cpa)} a conversion; weekends ${e.weekend_cpa ? `${usd(e.weekend_cpa)} a conversion` : "no conversions"} (${usd(e.weekend_cost)} spent). Last ${days} days.`, ...none };
    case "hour_of_day":
      return list(each(arr(e.hours).map((h: any) => `${h.label}: ${usd(h.cost)}`)), e.total);
    case "device":
      return { line: arr(e.devices).map((d: any) => `${d.label} ${n(d.share)}% of spend, ${perConversion(d.cpa)}`).join("; ") + `. Last ${days} days.`, ...none };
    case "call_hours":
      return list(each(arr(e.assets).map((a: any) => `${a.campaign || "Whole account"}${a.phone ? `, ${a.phone}` : ""}`)), e.total);
    case "rsa_assets":
      return list(each(arr(e.ads).map((a: any) => `${a.ad_group} in ${a.campaign}: ${arr(a.issues).join(", ")}`)), e.total);
    case "landing_utm":
      return list(each(arr(e.ads).map((a: any) => `${a.ad_group} in ${a.campaign}: no ${arr(a.missing).join(", ")}`)), e.total);
    case "audience":
      return list(each(arr(e.lists).map((l: any) => `${l.name}: ${l.open ? `${n(l.size).toLocaleString("en-US")} matched` : "closed"}`)), e.total);
    case "ai_max":
      return {
        line: e.search_cpa ? `Search: ${usd(e.search_cpa)} a conversion in ${days} days.` : "",
        more: arr(e.campaigns).map((c: any) => `${c.campaign}: ${c.cost === null || c.cost === undefined ? "no numbers yet" : perConversion(c.cpa)}`),
        moreLabel: arr(e.campaigns).length ? `Show ${count(arr(e.campaigns).length, "campaign")}` : "",
      };
    default:
      return { line: "", ...none };
  }
}

/** The numbers behind a finding, as one short line. */
export function evidenceLine(f: Finding): string {
  return evidence(f).line;
}

/** Checks the last run could not read, in words: "Change history in Junk King Sacramento (Search)". */
export function failedChecks(run: Pick<Run, "accounts"> | null, withAccount = true): string[] {
  if (!run) return [];
  return run.accounts.flatMap((a) => {
    const where = withAccount ? ` in ${accountLabel(a.customer_id, a.name)}` : "";
    return a.status === "failed" && a.error
      ? [`every check${where}`]
      : Object.keys(a.failed_checks || {}).map((k) => `${CHECK_LABEL[k] || k}${where}`);
  });
}
